// Workbench shell ("Build"): sidebar nav + nested routes.
//
// Frontend redesign v0.2 R2 (drafts/coframe_frontend_redesign_v0_2.md §4):
// the authoring flow is presented as FOUR phases rather than a flat
// 7-step pipeline —
//
//   1 · Connect & Scope   Collection · Columns
//   2 · Define            Declare (+ Lineage as an inline suggestion tool)
//   3 · Certify           the single fail-closed boundary (merges the
//                         former Attest + Verify steps)
//   4 · Publish           save + register into the data source
//
// R2.1 vocabulary (drafts/coframe_frontend_redesign_v0_2.md §0 D6 + §10):
// the first step is named "Collection" (you author a Collection; the
// underlying session is plumbing) and an *installation* is surfaced to
// users as a "data source" (engine + database). Code/API keep
// `session_id` / `installation_id`.
//
// Engine moves to a de-emphasised "operational" group — it's a
// runtime/cache concern, not an authoring step.
//
// Legacy slugs (tables, attest, verify) redirect so bookmarks resolve.
// All Build pages share session state via SessionProvider so each page
// can read "the current session" without prop-drilling.

import { Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { CertifyPage } from "./CertifyPage";
import { ColumnPoolPage } from "./ColumnPoolPage";
import { DeclarePage } from "./DeclarePage";
import { DiscoverPage } from "./DiscoverPage";
import { EnginePage } from "./EnginePage";
import { LineagePage } from "./LineagePage";
import { OperatorsPage } from "./OperatorsPage";
import { PublishPage } from "./PublishPage";
import { SessionPanel } from "./SessionPanel";
import { useSession } from "./SessionContext";

export function WorkbenchLayout() {
  // SessionProvider lives at the App root now (slice 6) so Collections
  // can read the same active session.
  return (
    <div style={shellStyle}>
      <Sidebar />
      <div style={mainStyle}>
        <Routes>
          <Route index element={<Navigate to="collection" replace />} />
          <Route path="collection" element={<SessionPanel />} />
          {/* Legacy slug — /workbench/session redirects to
              /workbench/collection (R2.1: the user-facing object you
              start on is the Collection; the session is plumbing). */}
          <Route path="session" element={<Navigate to="../collection" replace />} />
          <Route path="pool" element={<ColumnPoolPage />} />
          {/* Legacy slug — /workbench/tables now redirects to /workbench/pool. */}
          <Route path="tables" element={<Navigate to="../pool" replace />} />
          <Route path="operators" element={<OperatorsPage />} />
          <Route path="discover" element={<DiscoverPage />} />
          <Route path="lineage" element={<LineagePage />} />
          <Route path="declare" element={<DeclarePage />} />
          <Route path="certify" element={<CertifyPage />} />
          {/* Legacy slugs — Attest + Verify merged into Certify (R2). */}
          <Route path="attest" element={<Navigate to="../certify" replace />} />
          <Route path="verify" element={<Navigate to="../certify" replace />} />
          <Route path="publish" element={<PublishPage />} />
          <Route path="engine" element={<EnginePage />} />
        </Routes>
      </div>
    </div>
  );
}

interface NavItem {
  to: string;
  label: string;
  disabled?: boolean;
  hint?: string;
}

interface NavGroup {
  /** Phase number, e.g. "1". Omitted for the operational group. */
  ordinal?: string;
  title: string;
  items: NavItem[];
}

function Sidebar() {
  const { session } = useSession();
  const location = useLocation();

  const noSession = session === null;
  const gate = (extra?: Partial<NavItem>): Partial<NavItem> =>
    noSession ? { disabled: true, hint: "Start a Lattice first", ...extra } : (extra ?? {});

  const groups: NavGroup[] = [
    {
      ordinal: "1",
      title: "Connect & Scope",
      items: [
        { to: "collection", label: "Data Source", hint: "Pick a data source + name the Lattice" },
        { to: "pool", label: "Columns", ...gate() },
        { to: "operators", label: "Operators", ...gate({ hint: "The operator registry (Manual ch.10) + author overrides" }) },
      ],
    },
    {
      ordinal: "2",
      title: "Discover",
      items: [
        {
          to: "discover",
          label: "Discover",
          ...gate({ hint: "Auto-guess a draft column-spec per pooled column" }),
        },
        { to: "lineage", label: "Lineage", ...gate({ hint: "Suggestions for anchors / keys" }) },
      ],
    },
    {
      ordinal: "3",
      title: "Declare",
      items: [
        { to: "declare", label: "Declare", ...gate() },
      ],
    },
    {
      ordinal: "4",
      title: "Certify",
      items: [{ to: "certify", label: "Certify", ...gate() }],
    },
    {
      ordinal: "5",
      title: "Publish",
      items: [{ to: "publish", label: "Publish", ...gate() }],
    },
    {
      title: "Operational",
      items: [{ to: "engine", label: "Engine", ...gate({ hint: "Cache / engine stats" }) }],
    },
  ];

  return (
    <aside style={sidebarStyle}>
      <div style={sidebarSectionStyle}>Build</div>
      <nav>
        {groups.map((group) => (
          <div key={group.title} style={groupStyle}>
            <div style={groupHeaderStyle}>
              {group.ordinal !== undefined && (
                <span style={ordinalStyle}>{group.ordinal}</span>
              )}
              {group.title}
            </div>
            {group.items.map((it) => {
              const isActive = location.pathname.endsWith(`/${it.to}`);
              const baseStyle = it.disabled ? sidebarLinkDisabledStyle : sidebarLinkStyle;
              const style = isActive ? { ...baseStyle, ...sidebarLinkActiveStyle } : baseStyle;
              return (
                <div key={it.to}>
                  {it.disabled ? (
                    <span style={style} title={it.hint}>
                      {it.label}
                    </span>
                  ) : (
                    <Link to={it.to} style={style} title={it.hint}>
                      {it.label}
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </nav>
      <div style={sidebarFooterStyle}>
        {session === null ? (
          <span style={{ color: "#aaa" }}>no session</span>
        ) : (
          <>
            <div style={{ fontSize: "0.7rem", color: "#888" }}>Lattice</div>
            <div
              style={{
                fontWeight: 600,
                color: "#222",
                fontSize: "0.9rem",
                wordBreak: "break-word",
              }}
              title={`session_id ${session.session_id}`}
            >
              {session.ac_name}
            </div>
            <div style={{ fontSize: "0.7rem", color: "#888", marginTop: 4 }}>
              data source <code>{session.installation_id}</code>
            </div>
            <div style={{ marginTop: 4 }}>
              <span style={phaseBadgeStyle(session.scope_phase)}>
                {session.scope_phase}
              </span>
            </div>
          </>
        )}
      </div>
    </aside>
  );
}

// ── Styles ────────────────────────────────────────────────────────────

const shellStyle: React.CSSProperties = {
  display: "flex",
  gap: 0,
  alignItems: "stretch",
  minHeight: "calc(100vh - 4rem)",
};
const sidebarStyle: React.CSSProperties = {
  width: 200,
  flexShrink: 0,
  padding: "1rem 0",
  borderRight: "1px solid #ddd",
  background: "#fafafa",
  display: "flex",
  flexDirection: "column",
  gap: "0.5rem",
};
const sidebarSectionStyle: React.CSSProperties = {
  padding: "0 1rem",
  fontWeight: 600,
  color: "#444",
  fontSize: "0.85rem",
  textTransform: "uppercase",
  letterSpacing: "0.02em",
};
const groupStyle: React.CSSProperties = {
  marginTop: "0.85rem",
};
const groupHeaderStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.4rem",
  padding: "0 1rem 0.25rem 1rem",
  color: "#999",
  fontSize: "0.65rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};
const ordinalStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "1.05rem",
  height: "1.05rem",
  borderRadius: "50%",
  background: "#e3e8ef",
  color: "#5a6573",
  fontSize: "0.6rem",
  fontWeight: 700,
};
const sidebarLinkStyle: React.CSSProperties = {
  display: "block",
  padding: "0.45rem 1rem",
  color: "#222",
  textDecoration: "none",
  fontSize: "0.95rem",
  borderLeft: "3px solid transparent",
};
const sidebarLinkActiveStyle: React.CSSProperties = {
  background: "#eef4ff",
  borderLeftColor: "#0366d6",
  color: "#0366d6",
  fontWeight: 500,
};
const sidebarLinkDisabledStyle: React.CSSProperties = {
  ...sidebarLinkStyle,
  color: "#aaa",
  cursor: "not-allowed",
};
const sidebarFooterStyle: React.CSSProperties = {
  marginTop: "auto",
  padding: "0.75rem 1rem",
  borderTop: "1px solid #eee",
  fontSize: "0.75rem",
  color: "#666",
};
const mainStyle: React.CSSProperties = {
  flex: 1,
  padding: "1.5rem",
  minWidth: 0,
};

function phaseBadgeStyle(phase: string): React.CSSProperties {
  const isCommitted = phase === "committed";
  return {
    display: "inline-block",
    padding: "0.1rem 0.4rem",
    borderRadius: 3,
    fontSize: "0.7rem",
    background: isCommitted ? "#cfe8d9" : "#fff5d6",
    color: isCommitted ? "#1a6b3a" : "#8b6914",
    border: `1px solid ${isCommitted ? "#a5d8b8" : "#e6d8a8"}`,
  };
}
